/**
 * ZebraBuilder.js
 * The core web component that orchestrates the Zebra builder.
 */
import { ZebraApiService } from './ZebraApiService.js';
import { ZebraStateManager } from './ZebraStateManager.js';
import { ZebraUIFeedback } from './ZebraUIFeedback.js';
import { ZebraEventHandler } from './ZebraEventHandler.js';
import { ZebraStepOrchestrator  } from './ZebraStepOrchestrator.js';
import { ZebraProgressManager } from './ZebraProgressManager.js';
import { ZebraCardManager } from './ZebraCardManager.js';
import { ZebraModalManager } from './ZebraModalManager.js';
import { ZebraCartAndShareManager } from './ZebraCartAndShareManager.js';
import { ZebraUrlManager } from './ZebraUrlManager.js';

class ZebraBuilder extends HTMLElement {
    constructor() {
        super();

        // Initialize managers in the correct order
        this.stateManager = new ZebraStateManager();
        this.apiService = new ZebraApiService();
        this.uiFeedback = new ZebraUIFeedback(this);
        this.progressManager = new ZebraProgressManager(this);

        // Initialize managers that don't have dependencies first
        this.cartAndShareManager = new ZebraCartAndShareManager(this);

        // Create modalManager before cardManager since cardManager depends on it
        this.modalManager = new ZebraModalManager(this);

        // Now create cardManager with all its dependencies ready
        this.cardManager = new ZebraCardManager(this);

        // Create stepOrchestrator after all managers it might need
        this.stepOrchestrator = new ZebraStepOrchestrator(this);

        // Event handler last as it needs access to all other managers
        this.eventHandler = new ZebraEventHandler(this);

        // Initialize URL manager if it's used
        this.urlManager = new ZebraUrlManager(this);
    }

    /**
     * Web component lifecycle method. Fires when the element is added to the DOM.
     */
    connectedCallback() {
        this.initRefs();

        this.uiFeedback.showLoading();
        this.initialize();
        this.eventHandler.setupAllListeners();
    }

    /**
     * Web component lifecycle method. Fires when the element is removed from the DOM.
     */
    disconnectedCallback() {
        this.eventHandler.removeAllListeners();
        this.modalManager.removeGlobalListeners();
    }

    /**
     * Finds and stores references to essential STATIC DOM elements within the component.
     * References to dynamic elements (like the next button) will be re-established by the renderer.
     */
    initRefs() {
        this.contentContainer = this.querySelector('[data-builder-content]');
        this.progressContainer = this.querySelector('[data-progress-container]');
        this.backButton = this.querySelector('#back-btn');
        this.submitButton = this.querySelector('#inline-next-btn');
        this.minicart = document.querySelector('mini-cart');

        if (!this.contentContainer) {
            console.error('CRITICAL ERROR: The builder content container with attribute [data-builder-content] was not found in the DOM.');
        }
    }

    /**
     * The main initialization sequence for the builder.
     */
    async initialize() {
        this.uiFeedback.showLoading();
        this.stateManager.isInitializing = true;
        this.stateManager.loadStateFromURL();

        try {
            // Determine if the page was reloaded. This flag is used at the end of the function.
            const navEntries = performance.getEntriesByType("navigation");
            const isPageRefresh = navEntries.length > 0 && navEntries[0].type === 'reload';

            let apiPayload = {};

            if (this.stateManager.sharedConfigIdFromUrl) {
                // Load the snapshot data but do NOT modify it here.
                const snapshotData = await this.apiService.loadSnapshot(this.stateManager.sharedConfigIdFromUrl);
                this.stateManager.updateStateFromApiResponse(snapshotData);
                this.stateManager.wasStep2Skipped = this.stateManager.currentStep === 3 && !snapshotData.context?.['environment'];
            } else if (this.stateManager.contextFromUrl) {
                apiPayload = this.stateManager.contextFromUrl;
                if (this.stateManager.stepFromUrl) this.stateManager.currentStep = this.stateManager.stepFromUrl;
                const responseData = await this.apiService.fetchData(apiPayload);
                this.stateManager.updateStateFromApiResponse(responseData);
            } else {
                this.stateManager.initState();
                const responseData = await this.apiService.fetchData({});
                this.stateManager.updateStateFromApiResponse(responseData);
            }

            // This will render Step 3. The logic in ZebraCardManager will now handle the default selections.
            await this.stepOrchestrator.renderCurrentStep();

            // Only apply custom selections IF IT IS NOT A PAGE REFRESH.
            if (!isPageRefresh) {
                let selectionsToApply = this.stateManager.selectionsFromUrl || this.stateManager.context?.selections;

                if (selectionsToApply && this.stateManager.currentStep === 3) {
                    await this.urlManager.applySelectionsFromURL(selectionsToApply);
                }
            } else {
                // console.log('[ZebraBuilder] Page refresh detected. Custom selections will not be applied.');
            }

        } catch (error) {
            console.error('Failed to initialize ZebraBuilder:', error);
            this.uiFeedback.showError('There was a problem loading the builder. Please try refreshing.');
        } finally {
            this.stateManager.isInitializing = false;
            this.classList.remove('hidden');
            this.uiFeedback.hideLoading();

            const parentSection = this.closest('#zebra-builder-section');
            if (parentSection) {
                parentSection.classList.remove('hidden');
            } else {
                console.warn('[ZebraBuilder] Parent section not found');
            }
        }
    }

    /**
     * Sets up the sticky behavior for the mobile action bar in Step 3.
     */
    setupStickyMobileBar() {
        // Only run on mobile
        if (window.innerWidth >= 768) return;

        const mobileBar = document.getElementById('mobile-action-bar');
        const placeholder = document.getElementById('mobile-action-bar-placeholder');

        if (!mobileBar || !placeholder) return;

        // Get the header height to position the bar below it when sticky
        const header = document.getElementById('shopify-section-header');
        const headerHeight = header ? header.offsetHeight : 66; // Default header height if not found

        let originalTop = mobileBar.getBoundingClientRect().top + window.scrollY;
        const barHeight = mobileBar.offsetHeight;

        placeholder.style.height = `${barHeight}px`;

        const handleScroll = () => {
            if (window.innerWidth >= 768) {
                 mobileBar.classList.remove('fixed');
                 placeholder.style.display = 'none';
                return;
            };

            const scrollPosition = window.scrollY;

            if (scrollPosition > originalTop) {
                if (!mobileBar.classList.contains('fixed')) {
                    mobileBar.classList.add('fixed');
                    mobileBar.style.top = `${headerHeight}px`;
                    mobileBar.style.left = '0';
                    mobileBar.style.right = '0';
                    mobileBar.style.zIndex = '39';
                    placeholder.style.display = 'block';
                }
            } else {
                mobileBar.classList.remove('fixed');
                mobileBar.style.top = '';
                placeholder.style.display = 'none';
            }
        };

        // Use a single, named event listener to avoid duplicates
        if (!this._boundHandleScroll) {
            this._boundHandleScroll = handleScroll;
            window.addEventListener('scroll', this._boundHandleScroll);
        }
    }

    /**
     * Resets the tutorial for testing purposes
     */
    resetTutorial() {
        if (this.cardTutorial) {
            this.cardTutorial.resetTutorial();
        }
    }
}

customElements.define('zebra-builder', ZebraBuilder);
